import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuthStore } from '../../stores';
import { Button, Input, Card, LanguageSwitch } from '../shared';

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, error, login, clearError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Clear store error when component mounts or email/password change
  useEffect(() => {
    clearError();
  }, [clearError]);

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};

    if (!email.trim()) {
      errors.email = t('auth.login.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = t('auth.login.emailInvalid');
    }

    if (!password) {
      errors.password = t('auth.login.passwordRequired');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (error) {
      // Error is handled by the store and displayed via the error state
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-ivory flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <LanguageSwitch className="justify-end -mt-2 -mr-2 mb-2" />
        {/* Branding */}
        <div className="text-center mb-8">
          <div className="text-4xl mb-2">🐾</div>
          <h1 className="text-2xl font-bold text-gray-900">{t('auth.brand.name')}</h1>
          <p className="text-sm text-gray-600 mt-2">{t('auth.brand.tagline')}</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700 font-medium">{error}</p>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            label={t('auth.login.emailLabel')}
            name="email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setValidationErrors({ ...validationErrors, email: undefined });
            }}
            error={validationErrors.email}
            placeholder={t('auth.login.emailPlaceholder')}
            required
            autoComplete="email"
          />

          <Input
            label={t('auth.login.passwordLabel')}
            name="password"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setValidationErrors({ ...validationErrors, password: undefined });
            }}
            error={validationErrors.password}
            placeholder={t('auth.login.passwordPlaceholder')}
            required
            autoComplete="current-password"
          />

          <Button
            type="submit"
            variant="primary"
            size="md"
            fullWidth
            isLoading={isLoading}
            disabled={isLoading}
          >
            {t('auth.login.submit')}
          </Button>

          <p className="text-sm text-center mt-3">
            <Link
              to="/forgot-password"
              className="text-sage-600 hover:text-sage-700 font-medium hover:underline"
            >
              {t('auth.login.forgotPassword')}
            </Link>
          </p>
        </form>

        {/* Signup Link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            <Trans
              i18nKey="auth.login.signupPrompt"
              components={{
                signupLink: (
                  <Link
                    to="/signup"
                    className="text-sage-600 font-medium hover:text-sage-700 transition-colors"
                  />
                ),
              }}
            />
          </p>
        </div>
      </Card>
    </div>
  );
};
